import {changesetReducer} from '@microsoft/agent-host-protocol'
import type {ActionEnvelope, ChangesetFile, ChangesetState} from '@microsoft/agent-host-protocol'

export interface PersistedChangeset {
  readonly files: readonly ChangesetFile[]
  readonly status: ChangesetState['status'] | undefined
  readonly error?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFile(value: unknown): value is ChangesetFile {
  if (!isRecord(value) || typeof value['id'] !== 'string' || !value['id'] || !isRecord(value['edit'])) return false
  const edit = value['edit']
  if (!edit['before'] && !edit['after']) return false
  for (const side of [edit['before'], edit['after']]) {
    if (side === undefined) continue
    if (!isRecord(side) || typeof side['uri'] !== 'string' || !side['uri']) return false
    const content = side['content']
    if (!isRecord(content) || typeof content['uri'] !== 'string' || !content['uri']) return false
  }
  const diff = edit['diff']
  return (
    diff === undefined ||
    (isRecord(diff) &&
      ['added', 'removed'].every(
        key =>
          diff[key] === undefined || (typeof diff[key] === 'number' && Number.isFinite(diff[key]) && diff[key] >= 0),
      ))
  )
}

/** Keep producer ordering: server sequences restart when the host moves to a new epoch. */
export function replayChangesetActions(envelopes: readonly ActionEnvelope[]): Record<string, PersistedChangeset> {
  const states = new Map<string, ChangesetState>()
  const statusKnown = new Set<string>()
  const errors = new Map<string, string>()
  for (const {channel, action} of envelopes) {
    if (!action.type.startsWith('changeset/')) continue
    const current: ChangesetState = states.get(channel) ?? {status: 'computing' as ChangesetState['status'], files: []}
    let valid = true
    switch (action.type) {
      case 'changeset/fileSet':
        valid = isFile(action.file)
        break
      case 'changeset/contentChanged':
        valid = Array.isArray(action.files) && action.files.every(isFile)
        break
      case 'changeset/fileRemoved':
        valid = typeof action.fileId === 'string'
        break
      case 'changeset/statusChanged':
        valid =
          ['computing', 'ready', 'error'].includes(action.status) &&
          (action.error === undefined ||
            (isRecord(action.error) &&
              typeof action.error['errorType'] === 'string' &&
              typeof action.error['message'] === 'string'))
        if (valid) statusKnown.add(channel)
        break
      case 'changeset/cleared':
        break
      default:
        // Operations and review state do not establish saved file metadata.
        continue
    }
    if (!valid) {
      errors.set(
        channel,
        'Some information about changed files may be incomplete. Reconnect to refresh the changed files.',
      )
      continue
    }
    states.set(channel, changesetReducer(current, action))
    if (action.type === 'changeset/contentChanged' || action.type === 'changeset/cleared') errors.delete(channel)
  }
  const result: Record<string, PersistedChangeset> = {}
  for (const channel of new Set([...states.keys(), ...errors.keys()])) {
    const state = states.get(channel)
    result[channel] = {
      files: state?.files ?? [],
      status: statusKnown.has(channel) ? state?.status : undefined,
      error: errors.get(channel) ?? state?.error?.message,
    }
  }
  return result
}
